import classNames from 'classnames'
import _ from 'lodash'
import moment from 'moment'
import React from 'react'

import { IInputProps } from 'browser/components/atomic-elements/atoms/input/input'
import { List } from 'browser/components/atomic-elements/atoms/list'
import { IRenderListItemProps } from 'browser/components/atomic-elements/atoms/list/abstract-list'
import 'browser/components/atomic-elements/molecules/fields/hours-of-operation-field/_hours-of-operation-input.scss'
import {
  DayOfWeek,
  HoursOfOperationItem,
  HoursOfOperationRow,
} from 'browser/components/atomic-elements/molecules/fields/hours-of-operation-field/hours-of-operation-row'
import {
  DateTimeInterval,
  HoursOfOperation,
} from 'shared-libs/generated/server-types/entity/locationEntity'
import { Formatter } from 'shared-libs/helpers/formatter'

/**
 * @uiComponent
 */
export interface IHoursOfOperationInputProps extends IInputProps {
  showEditableFields?: boolean
  errorsMap?: any
}

interface IHoursOfOperationInputtState {
  hoursOfOperationByDay: Record<DayOfWeek, string> & Partial<Record<string, string>>
}

export class HoursOfOperationInput extends React.Component<
  IHoursOfOperationInputProps,
  IHoursOfOperationInputtState
> {
  constructor(props: IHoursOfOperationInputProps) {
    super(props)
    this.state = this.getNextStateForProps(props)
  }

  public UNSAFE_componentWillReceiveProps(nextProps: IHoursOfOperationInputProps) {
    const { showEditableFields, value } = nextProps
    if (showEditableFields && value && this.props.value !== value) {
      this.setState(this.getNextStateForProps(nextProps))
    }
  }

  public render() {
    const { showEditableFields, value } = this.props
    const content = showEditableFields ? this.renderContent(value) : this.renderStaticContent()

    return <div className="c-hoursOfOperation">{content}</div>
  }

  private getNextStateForProps(props: IHoursOfOperationInputProps) {
    const value: HoursOfOperation | undefined = props.value
    const hoursByDay: Record<DayOfWeek, DateTimeInterval[]> = {
      Sunday: [],
      // tslint:disable-next-line:object-literal-sort-keys
      Monday: [],
      Tuesday: [],
      Wednesday: [],
      Thursday: [],
      Friday: [],
      Saturday: [],
    }
    // lodash iterator keys are strings, so the open-day lookup is keyed by string
    const open24HoursDays = new Set<string>()
    _.forEach(value, ({ isOpen24Hours, daysOfWeek, hours }) => {
      _.forEach(daysOfWeek, (dayOfWeek) => {
        if (isOpen24Hours || open24HoursDays.has(dayOfWeek)) {
          open24HoursDays.add(dayOfWeek)
        } else {
          hoursByDay[dayOfWeek] = hoursByDay[dayOfWeek].concat(hours || [])
        }
      })
    })
    const formatDay = (dayOfWeek: DayOfWeek) => {
      let hours = hoursByDay[dayOfWeek]
      // TODO(peter/david/louis): need to distinguish between no data and closed
      // If no data then show
      // "None listed" - https://www.dropbox.com/s/6ruoqaptiepmwoe/Screenshot%202017-10-04%2013.40.50.png?dl=0
      if (open24HoursDays.has(dayOfWeek)) {
        return 'Open 24 hours'
      } else if (_.isEmpty(hours)) {
        return '-'
        // return 'Closed'
      }
      hours = hours.sort((left: any, right: any) => {
        return moment(left.start).isBefore(moment(right.start)) ? -1 : 1
      })
      return _.map(hours, (interval) => Formatter.formatTimeInterval(interval, false)).join(', ')
    }
    const hoursOfOperationByDay: IHoursOfOperationInputtState['hoursOfOperationByDay'] = {
      Sunday: formatDay('Sunday'),
      Monday: formatDay('Monday'),
      Tuesday: formatDay('Tuesday'),
      Wednesday: formatDay('Wednesday'),
      Thursday: formatDay('Thursday'),
      Friday: formatDay('Friday'),
      Saturday: formatDay('Saturday'),
    }
    // unknown open-24h day keys stay in state, as the original sentinel map kept them
    open24HoursDays.forEach((dayOfWeek) => {
      hoursOfOperationByDay[dayOfWeek] = 'Open 24 hours'
    })
    return {
      hoursOfOperationByDay,
    }
  }

  private renderStaticContent() {
    const { size } = this.props
    const { hoursOfOperationByDay } = this.state
    const sizeClass = _.isEmpty(size) ? 'c-fakeInputContainer' : `c-fakeInputContainer--${size}`
    return (
      <div className={classNames(sizeClass)}>
        <table className="c-table c-table--noBorders c-hoursOfOperationTable">
          <tbody className="c-table-body">
            <tr>
              <td className="u-innerBumperRight--xs">Sunday</td>
              <td>{hoursOfOperationByDay.Sunday}</td>
            </tr>
            <tr>
              <td className="u-innerBumperRight--xs">Monday</td>
              <td>{hoursOfOperationByDay.Monday}</td>
            </tr>
            <tr>
              <td className="u-innerBumperRight--xs">Tuesday</td>
              <td>{hoursOfOperationByDay.Tuesday}</td>
            </tr>
            <tr>
              <td className="u-innerBumperRight--xs">Wednesday</td>
              <td>{hoursOfOperationByDay.Wednesday}</td>
            </tr>
            <tr>
              <td className="u-innerBumperRight--xs">Thursday</td>
              <td>{hoursOfOperationByDay.Thursday}</td>
            </tr>
            <tr>
              <td className="u-innerBumperRight--xs">Friday</td>
              <td>{hoursOfOperationByDay.Friday}</td>
            </tr>
            <tr>
              <td className="u-innerBumperRight--xs">Saturday</td>
              <td>{hoursOfOperationByDay.Saturday}</td>
            </tr>
          </tbody>
        </table>
      </div>
    )
  }

  private renderContent(value: HoursOfOperation | undefined) {
    const { onChange, size } = this.props
    return (
      <List
        addButtonText="Add another set of days"
        onChange={onChange}
        renderListItem={this.renderSetOfDays}
        showPlaceholderItem={true}
        showAddButton={false}
        size={size}
        value={value}
      />
    )
  }

  private renderSetOfDays = (props: IRenderListItemProps) => {
    const { item, index } = props
    const { errorsMap } = this.props
    const handleChange = (value: HoursOfOperationItem) => this.handleItemChange(item, index)
    return (
      <HoursOfOperationRow
        errorsMap={errorsMap ? errorsMap[index] : null}
        onChange={handleChange}
        value={item}
      />
    )
  }

  private handleItemChange = (item: HoursOfOperationItem, index: number) => {
    const { onChange } = this.props
    const value: HoursOfOperation = this.props.value || []
    value[index] = item
    if (!onChange) {
      throw new TypeError('onChange is not a function')
    }
    onChange(value)
  }
}
